import { useState } from "react";
import { Grid, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Box, Typography } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { addSupplementApi, editSupplementApi } from "../apiServices";
import { globalPostService, globalPutService } from "../../../../utils/globalApiServices";
import { checkApiStatus, getTextFieldRows } from "../../../../utils";
import moment from "moment";
import { AttachementsUpload, ButtonLoader } from "../../../../shared_elements/components";
import { backendDateFormat, fieldDateFormat } from "../../../../constants";
import { useParams } from "react-router";
import { useSnackbar } from "notistack";
import { setFileUploadDetails } from "../../../../shared_elements/actions";
import { useDispatch, useSelector } from "react-redux";
import { trackActivity } from "../../../../utils/mixpanel";
import { formatBytes } from "../../../../utils/fileUpload";
import { regexConstants } from "../../../../constants/regEx";


const SupplementCRU = ({ addEditSupplement,fileUpload, getResponseBack, onToggleModal, onOpenAttachments, modeType }) => {
  const params = useParams();
  const { storageDetails } = useSelector((state) => state.leases);
  const dispatch = useDispatch()
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setLoading] = useState(false);
  const [data, setData] = useState(addEditSupplement?.data);
  const [error, setError] = useState({});
  const [attachments, setAttachments] = useState([]);

  const onRestErrorKey = (key) => {
    setError((prev) => ({ ...prev, [key]: '' }));
  };

  const onFieldChange = (key, value) => {
    setData({ ...data, [key]: value });
    setError((prev) => ({ ...prev, [key]: '' }));
  };

  const handleFileChange = (files) => {
    setAttachments(files);
    dispatch(setFileUploadDetails({attachments:files}))
  };

 const onAddEditSupplement = () => {
    const validationInputs = {
      date: data?.date ? "" : "Please select Supplement Date",
      unique_identifier: data?.unique_identifier?.trim()?.length ? "" : "Please enter Supplement Number",
      executed_by: data?.executed_by?.trim()?.length ? "" : "Please enter Executed By",
    };

    const supplementDate = moment(data?.date);
    if(data?.date && !supplementDate.isValid()) {
      validationInputs.date = "Please enter valid Supplement Date";
    }
  
    if (Object.keys(validationInputs).every((k) => validationInputs[k] === "")) {
      setLoading(true);
      
      if (data.id) {
        globalPutService(editSupplementApi({ ...params, slug: data?.slug }), data).then(async (response) => {
          if (checkApiStatus(response)) {
            if (attachments?.length) {
              fileUpload(data?.slug,'updated')
            }
            enqueueSnackbar(response?.data?.message, { variant: "success", anchorOrigin: { horizontal: "right", vertical: "top" } });
            onToggleModal();
            getResponseBack();
            setLoading(false);
            trackActivity('Item Edited', { page_title: `Edit Supplement`, success_msg: response?.data?.message, item_id: data?.id, payload: data, response: response?.data })
          } else {
            setLoading(false);
            enqueueSnackbar(response.data.message, { variant: "error", anchorOrigin: { horizontal: "right", vertical: "top" } });
            trackActivity('Item Edited', { page_title: `Edit Supplement`, error_msg: response?.data?.message, item_id: data?.id, payload: data, response: response?.data })
          }
        });
      } else {
        const { lessee, lessor, ...cleanedData } = data;
        globalPostService(addSupplementApi(params), { ...cleanedData, lessee_id: data?.lessee, lessor_id: data?.lessor }).then(async (response) => {
          if (checkApiStatus(response)) {
            if (attachments?.length) {
              fileUpload(response?.data?.data?.slug,'created')
            }
            enqueueSnackbar(response?.data?.message, { variant: "success", anchorOrigin: { horizontal: "right", vertical: "top" } });
            onToggleModal();
            getResponseBack();
            setLoading(false);
            trackActivity('Item Added', { page_title: `Add Supplement`, success_msg: response?.data?.message, item_id: response?.data?.data?.id, payload: data, response: response?.data })
          } else {
            setLoading(false);
            enqueueSnackbar(response?.data?.message, { variant: "error", anchorOrigin: { horizontal: "right", vertical: "top" } });
            trackActivity('Item Added', { page_title: `Add Supplement`, error_msg: response?.data?.message, item_id: null, payload: data, response: response?.data })
          }
        });
      }
    } else {
      setError(validationInputs);
      trackActivity(`Item ${modeType === 'add' ? 'Added' : 'Edited'}`, { page_title: `${modeType === 'add' ? 'Add' : modeType === 'edit' ? 'Edit' : 'View'} Supplement`, error_msg: validationInputs, })
    }
  };
  const handleItemClicked = (page_type) => {
    trackActivity('Item Clicked', { page_title: `${modeType === 'add' ? 'Add' : modeType === 'edit' ? 'Edit' : 'View'} Supplement`, page_type: page_type, })
  }

  return (
    <Dialog
      onClose={(event, reason) => { if (reason !== 'backdropClick') onToggleModal(); }}
      disableEscapeKeyDown
      aria-labelledby="customized-dialog-title"
      className="add-lease-dialog"
      maxWidth="md"
      fullWidth
      open={addEditSupplement.modal}
    >
      <DialogTitle sx={{ m: 0, p: 2 }} className='dialog-header'>
        <Typography className="dialog-header-title">
          {data?.id ? `${addEditSupplement.mode === "view" ? "View" : "Edit"} Supplement` : "Add Supplement"}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid size={{ md: 6, xs: 12 }}>
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DatePicker
                label="Supplement Date"
                value={data?.date ? moment(data.date) : null}
                onChange={(value) => {
                  const isValid = moment(value)?.isValid();
                  onFieldChange("date", value ? moment(value).format(backendDateFormat) : null)
                  if (isValid) onRestErrorKey("date");
                }}
                slotProps={{
                  textField: { required: true, InputLabelProps: { shrink: true }, size: "small", margin: "normal", error: error.date || false, helperText: error.date || '' },
                  field: { clearable: true, onClear: () => { onFieldChange("date", null); onRestErrorKey("date"); } },
                }}
                sx={{ width: "100%" }}
                format={fieldDateFormat}
                disabled={modeType === 'view'}
              />
            </LocalizationProvider>
          </Grid>
          <Grid size={{ md: 6, xs: 12 }}>
            <TextField
              disabled={addEditSupplement.mode === "view"}
              required
              id="unique_identifier"
              label="Supplement Number"
              variant="outlined"
              size="small"
              margin="normal"
              value={data?.unique_identifier || ""}
              onChange={(e) => regexConstants.restrictUnicodeCharacters.test(e.target.value) || e.target.value === "" ? onFieldChange("unique_identifier", e.target?.value.trimStart()) : e.preventDefault()}
              onFocus={() => onRestErrorKey("unique_identifier")}
              error={Boolean(error.unique_identifier)}
              helperText={error.unique_identifier || (data?.unique_identifier?.length > 25 ? "Maximum 25 characters allowed" : "")}
              slotProps={{ inputLabel: { shrink: true }, htmlInput: {maxLength: 25} }}
              fullWidth
            />
          </Grid>
          <Grid size={{ md: 12, xs: 12 }}>
            <TextField
              disabled={addEditSupplement.mode === "view"}
              required
              id="executed_by"
              label="Executed By"
              variant="outlined"
              size="small"
              margin="normal"
              value={data?.executed_by || ""}
              onChange={(e) => {
                const value = e.target.value;
                return regexConstants.restrictUnicodeCharacters.test(value) || value === "" ? onFieldChange("executed_by", value.trimStart()) : e.preventDefault()
              }}
              error={Boolean(error.executed_by)}
              helperText={error.executed_by || (data?.executed_by?.length > 255 ? "Maximum 255 characters allowed" : "")}
              fullWidth
              multiline
              slotProps={{ inputLabel: { shrink: true }, htmlInput: {maxLength: 255} }}
              rows={getTextFieldRows(data?.executed_by, 3, 100)}
            />
          </Grid>
          <Grid size={{ md: 12, xs: 12 }} sx={{ mt: 3 }}>
             <Box className="responsive-buttons">
            {data?.id && data?.attachments?.length ?
              <Button onClick={() => { onOpenAttachments(); handleItemClicked('ViewAttachments Button') }} size='small' variant='outlined' color='primary' sx={{ marginBottom: '10px' }}>({addEditSupplement?.data?.attachments?.length})Attachment(s)</Button> : null
            }
            <Button className="available-space-btn" disableRipple size='small' variant='outlined' color='primary'>{`Available Space : ${formatBytes(storageDetails?.remaining_storage)}`}</Button>
          </Box>
            {modeType === 'view' ? null : <AttachementsUpload onFileChange={handleFileChange} attachments={data?.attachments} remainingStorage={storageDetails?.remaining_storage} />}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions className="dialog-action">
        <Button className="cancel-btn" disabled={isLoading} variant="outlined" size="small" onClick={() => { onToggleModal(); handleItemClicked('Cancel Button') }}>{modeType === "view" ? "Close" : "Cancel"}</Button>
        {modeType === "view" ? null :
          <Button variant="contained" size="small" className="btn-primary" onClick={() => { handleItemClicked('Save Button'); onAddEditSupplement() }}> {isLoading ? <ButtonLoader /> : "SAVE"}</Button>}
      </DialogActions>
    </Dialog>
  );
};
export default SupplementCRU;
